import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Wifi, WifiOff } from 'lucide-react';
import Header from './components/Header';
import DataTable from './components/DataTable';
import ConnectionTest from './components/ConnectionTest';
import { speedcheckService } from './services/pocketbase';

const showManualSpeedtest = false;

function App() {
  const [allData, setAllData] = useState([]);
  const [latestRecords, setLatestRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('testing');
  const [showConnectionTest, setShowConnectionTest] = useState(false);
  const [runDomain, setRunDomain] = useState('display-sales.de');
  const [runningTest, setRunningTest] = useState(false);
  const [runMessage, setRunMessage] = useState('');

  const testConnection = async () => {
    try {
      setConnectionStatus('testing');
      await speedcheckService.testConnection();
      setConnectionStatus('connected');
      return true;
    } catch (err) {
      console.error('Connection test failed:', err);
      setConnectionStatus('failed');
      setError(`Connection test failed: ${err.message}`);
      return false;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Test connection first
      const isConnected = await testConnection();
      if (!isConnected) {
        setLoading(false);
        return;
      }
      
      const allRecordsData = await speedcheckService.getAllRecords();

      setAllData(allRecordsData);
      setLatestRecords(speedcheckService.latestByDomainAndTool(allRecordsData));
      setLastSync(new Date());
      setConnectionStatus('connected');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to fetch data: ${err.message}`);
      setConnectionStatus('failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Set up auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  const handleRunSpeedcheck = async () => {
    setRunningTest(true);
    setRunMessage('');

    try {
      await speedcheckService.runSpeedcheck(runDomain, 'pagespeed');
      await fetchData();
      setRunMessage('Speedtest gespeichert.');
    } catch (err) {
      setRunMessage(err.message);
    } finally {
      setRunningTest(false);
    }
  };

  const handleConnectionSuccess = () => {
    setShowConnectionTest(false);
    fetchData();
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <WifiOff className="w-5 h-5 text-red-500" />;
      case 'testing':
        return <div className="w-5 h-5 bg-yellow-500 rounded-full animate-pulse"></div>;
      default:
        return <WifiOff className="w-5 h-5 text-gray-400" />;
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'failed':
        return 'Connection failed';
      case 'testing':
        return 'Testing connection...';
      default:
        return 'Unknown status';
    }
  };

  if (error && connectionStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">S</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Display-Sales Speedcheck</h1>
            </div>
            <p className="text-gray-600">Performance Monitoring Dashboard</p>
          </div>

          <div className="mb-6">
            <div className="card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <h2 className="text-lg font-semibold text-gray-900">Connection Error</h2>
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleRefresh}
                  className="btn-primary"
                >
                  Try Again
                </button>
                <button
                  onClick={() => setShowConnectionTest(!showConnectionTest)}
                  className="btn-secondary"
                >
                  {showConnectionTest ? 'Hide' : 'Show'} Connection Test
                </button>
              </div>
            </div>
          </div>

          {showConnectionTest && (
            <ConnectionTest onConnectionSuccess={handleConnectionSuccess} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with latest metrics */}
      <Header latestRecords={latestRecords} loading={loading} />
      
      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status bar */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {getConnectionStatusIcon()}
              <span className="text-sm text-gray-600">
                {getConnectionStatusText()}
              </span>
            </div>
            
            {lastSync && connectionStatus === 'connected' && (
              <span className="text-sm text-gray-500">
                Last sync: {lastSync.toLocaleTimeString()}
              </span>
            )}
          </div>

          {showManualSpeedtest && (
          <div className="flex items-center space-x-3">
            <select
              value={runDomain}
              onChange={(event) => setRunDomain(event.target.value)}
              disabled={runningTest}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="display-sales.de">display-sales.de</option>
              <option value="display-sales.at">display-sales.at</option>
            </select>
            <button
              onClick={handleRunSpeedcheck}
              disabled={runningTest}
              className="btn-primary"
            >
              {runningTest ? 'Speedtest läuft…' : 'Speedtest starten'}
            </button>
            <span className="text-sm text-gray-500">PageSpeed zur vollen Stunde</span>
            {runMessage && (
              <span className={`text-sm ${runMessage === 'Speedtest gespeichert.' ? 'text-green-600' : 'text-red-600'}`}>
                {runMessage}
              </span>
            )}
          </div>
          )}
        </div>

        {/* Data table */}
        <DataTable 
          data={allData} 
          loading={loading} 
          onRefresh={handleRefresh}
        />
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">S</span>
              </div>
              <span className="text-sm text-gray-600">Display-Sales Speedcheck v1.0</span>
            </div>
            
            <div className="text-sm text-gray-500">
              © 2025 by Winning Solutions
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
